// Shared DMS ticketing constants mirrored from the backend ticket lifecycle.
// Lifecycle: open -> assigned -> in_progress -> resolved -> verified -> closed
// (resolved/verified may also be reopened back to in_progress).

import { ROLES, rankOf } from "./roles";

export const TICKET_STATUSES = {
  OPEN: "open",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  RESOLVED: "resolved",
  VERIFIED: "verified",
  CLOSED: "closed",
};

// Human-readable label per status.
export const TICKET_STATUS_LABEL = {
  [TICKET_STATUSES.OPEN]: "Open",
  [TICKET_STATUSES.ASSIGNED]: "Assigned",
  [TICKET_STATUSES.IN_PROGRESS]: "In Progress",
  [TICKET_STATUSES.RESOLVED]: "Resolved",
  [TICKET_STATUSES.VERIFIED]: "Verified",
  [TICKET_STATUSES.CLOSED]: "Closed",
};

// antd Tag color per status (semantic antd presets).
export const TICKET_STATUS_TAG_COLOR = {
  [TICKET_STATUSES.OPEN]: "red",
  [TICKET_STATUSES.ASSIGNED]: "orange",
  [TICKET_STATUSES.IN_PROGRESS]: "blue",
  [TICKET_STATUSES.RESOLVED]: "cyan",
  [TICKET_STATUSES.VERIFIED]: "geekblue",
  [TICKET_STATUSES.CLOSED]: "green",
};

// Options for the status filter dropdown.
export const TICKET_STATUS_OPTIONS = [
  { value: TICKET_STATUSES.OPEN, label: TICKET_STATUS_LABEL.open },
  { value: TICKET_STATUSES.ASSIGNED, label: TICKET_STATUS_LABEL.assigned },
  { value: TICKET_STATUSES.IN_PROGRESS, label: TICKET_STATUS_LABEL.in_progress },
  { value: TICKET_STATUSES.RESOLVED, label: TICKET_STATUS_LABEL.resolved },
  { value: TICKET_STATUSES.VERIFIED, label: TICKET_STATUS_LABEL.verified },
  { value: TICKET_STATUSES.CLOSED, label: TICKET_STATUS_LABEL.closed },
];

export const TICKET_PRIORITIES = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

export const TICKET_PRIORITY_LABEL = {
  [TICKET_PRIORITIES.LOW]: "Low",
  [TICKET_PRIORITIES.MEDIUM]: "Medium",
  [TICKET_PRIORITIES.HIGH]: "High",
  [TICKET_PRIORITIES.CRITICAL]: "Critical",
};

// antd Tag color per priority.
export const TICKET_PRIORITY_TAG_COLOR = {
  [TICKET_PRIORITIES.LOW]: "default",
  [TICKET_PRIORITIES.MEDIUM]: "blue",
  [TICKET_PRIORITIES.HIGH]: "orange",
  [TICKET_PRIORITIES.CRITICAL]: "red",
};

export const TICKET_PRIORITY_OPTIONS = [
  { value: TICKET_PRIORITIES.LOW, label: TICKET_PRIORITY_LABEL.low },
  { value: TICKET_PRIORITIES.MEDIUM, label: TICKET_PRIORITY_LABEL.medium },
  { value: TICKET_PRIORITIES.HIGH, label: TICKET_PRIORITY_LABEL.high },
  { value: TICKET_PRIORITIES.CRITICAL, label: TICKET_PRIORITY_LABEL.critical },
];

// Ticket types mirror the alert/health types plus manual ones.
export const TICKET_TYPES = {
  OFFLINE: "OFFLINE",
  IMAGE_STALE: "IMAGE_STALE",
  WARNING: "WARNING",
  NEVER_REPORTED: "NEVER_REPORTED",
  REBOOT: "REBOOT",
  MAINTENANCE: "MAINTENANCE",
  OTHER: "OTHER",
};

export const TICKET_TYPE_LABEL = {
  [TICKET_TYPES.OFFLINE]: "Offline",
  [TICKET_TYPES.IMAGE_STALE]: "Image Stale",
  [TICKET_TYPES.WARNING]: "Warning",
  [TICKET_TYPES.NEVER_REPORTED]: "Never Reported",
  [TICKET_TYPES.REBOOT]: "Reboot",
  [TICKET_TYPES.MAINTENANCE]: "Maintenance",
  [TICKET_TYPES.OTHER]: "Other",
};

// antd Tag color per type.
export const TICKET_TYPE_TAG_COLOR = {
  [TICKET_TYPES.OFFLINE]: "red",
  [TICKET_TYPES.IMAGE_STALE]: "gold",
  [TICKET_TYPES.WARNING]: "gold",
  [TICKET_TYPES.NEVER_REPORTED]: "red",
  [TICKET_TYPES.REBOOT]: "purple",
  [TICKET_TYPES.MAINTENANCE]: "blue",
  [TICKET_TYPES.OTHER]: "default",
};

export const TICKET_TYPE_OPTIONS = [
  { value: TICKET_TYPES.OFFLINE, label: TICKET_TYPE_LABEL.OFFLINE },
  { value: TICKET_TYPES.IMAGE_STALE, label: TICKET_TYPE_LABEL.IMAGE_STALE },
  { value: TICKET_TYPES.WARNING, label: TICKET_TYPE_LABEL.WARNING },
  {
    value: TICKET_TYPES.NEVER_REPORTED,
    label: TICKET_TYPE_LABEL.NEVER_REPORTED,
  },
  { value: TICKET_TYPES.REBOOT, label: TICKET_TYPE_LABEL.REBOOT },
  { value: TICKET_TYPES.MAINTENANCE, label: TICKET_TYPE_LABEL.MAINTENANCE },
  { value: TICKET_TYPES.OTHER, label: TICKET_TYPE_LABEL.OTHER },
];

// Rank thresholds mirroring backend authorization.
const SUPERVISOR_RANK = rankOf(ROLES.SUPERVISOR); // 2

// Lifecycle actions a user may take for a given ticket status, given their
// role and whether they are the current assignee. Pure function — keep it
// testable and free of side effects. The backend still enforces every rule;
// this only governs which buttons render.
//
// Returns an array of { key, label, toStatus } where toStatus is the status
// the transition moves the ticket to (null for non-transition actions, but
// every action here is a transition).
export const availableActions = (status, role, isAssignee) => {
  const rank = rankOf(role);
  // Supervisors have full ticket lifecycle control (same as hardwareAdmin).
  const isSupervisor = rank >= SUPERVISOR_RANK;
  const actions = [];

  switch (status) {
    case TICKET_STATUSES.OPEN:
      if (isSupervisor) {
        actions.push({
          key: "assign",
          label: "Assign",
          toStatus: TICKET_STATUSES.ASSIGNED,
        });
        actions.push({
          key: "close",
          label: "Close / Cancel",
          toStatus: TICKET_STATUSES.CLOSED,
        });
      }
      break;

    case TICKET_STATUSES.ASSIGNED:
      if (isAssignee || isSupervisor) {
        actions.push({
          key: "start",
          label: "Start",
          toStatus: TICKET_STATUSES.IN_PROGRESS,
        });
      }
      if (isSupervisor) {
        actions.push({
          key: "reassign",
          label: "Reassign",
          toStatus: TICKET_STATUSES.ASSIGNED,
        });
      }
      break;

    case TICKET_STATUSES.IN_PROGRESS:
      if (isAssignee || isSupervisor) {
        actions.push({
          key: "resolve",
          label: "Resolve",
          toStatus: TICKET_STATUSES.RESOLVED,
        });
      }
      if (isSupervisor) {
        actions.push({
          key: "reassign",
          label: "Reassign",
          toStatus: TICKET_STATUSES.ASSIGNED,
        });
      }
      break;

    case TICKET_STATUSES.RESOLVED:
      if (isSupervisor) {
        actions.push({
          key: "verify",
          label: "Verify",
          toStatus: TICKET_STATUSES.VERIFIED,
        });
        actions.push({
          key: "reopen",
          label: "Reopen",
          toStatus: TICKET_STATUSES.IN_PROGRESS,
        });
      }
      break;

    case TICKET_STATUSES.VERIFIED:
      if (isSupervisor) {
        actions.push({
          key: "close",
          label: "Close",
          toStatus: TICKET_STATUSES.CLOSED,
        });
        actions.push({
          key: "reopen",
          label: "Reopen",
          toStatus: TICKET_STATUSES.IN_PROGRESS,
        });
      }
      break;

    case TICKET_STATUSES.CLOSED:
    default:
      break;
  }

  return actions;
};

// Actions that should prompt the user for an optional note before submitting.
export const NOTE_PROMPT_ACTIONS = new Set([
  "resolve",
  "verify",
  "close",
  "reopen",
]);
